import Vue from "vue";
import {
  ObterProtocolos,
  DetalheProtocolo,
  VerificarProtocoloReclamacao,
  SolicitarOuvidoria,
  ObterImagem,
  ObterDocumento,
  RetornoPendencia
} from "@/api/ouvidoria";
import { checkUpdatePermission } from "@/utils/checkReadonlyUtil";
const initialState = {
  complaints: [],
  lastFetch: null,
  successRequest: false,
  filters: {},
  activedSort: "",
  sorts: ["Mais recentes", "Mais antigos"],
  newSolicitation: {
    protocolo: "",
    observacao: "",
    anexosN: []
  }
};

export default {
  namespaced: true,
  state: () => ({ ...initialState }),
  actions: {
    async fetchComplaints(context, options) {
      const forceFetch = options?.force ?? false;
      if (!forceFetch && context.state.lastFetch) return;

      const response = await ObterProtocolos({
        vinculo: context.rootState.saude.vinculo,
        classificacao: 10508
      });
      if (response?.Data?.length) {
        let protocols = response.Data.map(p => ({
          protocol: p.ProtocoloANS,
          ticket: p.Chamado,
          title: p.AssuntoOuvidoria,
          beneficiaryCode: p.CdSituacao,
          beneficiaryName: p.DsSolicitante,
          statusCode: p.CdSituacao,
          statusDsc: p.DsSituacao,
          createdAt: p.DtOuvidoria,
          canEvaluate: p.Nota === null
        }));
        context.commit("SET_COMPLAINTS", protocols);
      }
    },
    async fetchComplaintDetail(context, { protocol }) {
      const response = await DetalheProtocolo({
        vinculo: context.rootState.saude.vinculo,
        protocolo: protocol,
        classificacao: 10532
      });
      if (response.Data) {
        return {
          protocol: response.Data.ProtocoloANS,
          ticket: response.Data.Chamado,
          title: response.Data.AssuntoOuvidoria,
          beneficiaryCode: response.Data.CdSolicitante,
          beneficiaryName: response.Data.DsSolicitante,
          statusCode: response.Data.CdSituacao,
          statusDsc: response.Data.DsSituacao,
          createdAt: response.Data.DtOuvidoria,
          description: response.Data.Descricao,
          doc: {
            id: response.Data.Documento.Id ?? 0,
            title: response.Data.Documento.Descricao,
            createdAt: response.Data.Documento.Data,
            resp: response.Data.Documento.Resposta ?? []
          },
          pendency: {
            ticket: response.Data.Pendencia?.Chamado ?? 0,
            reasons: response.Data.Pendencia?.Motivos ?? [],
            days: response.Data.Pendencia.DiasResposta ?? 4
          },
          score: response.Data.Nota,
          pictures: response.Data.Imagens
        };
      }
    },
    async fetchProtocolReclamation(context, { protocol }) {
      const response = await VerificarProtocoloReclamacao({
        vinculo: context.rootState.saude.vinculo,
        protocolo: protocol
      });
      if (response.Data) {
        return {
          protocol: response.Data.Protocolo,
          ticket: response.Data.Chamado,
          statusCode: response.Data.Validacao,
          responseAt: response.Data.PrevisaoRetorno ?? "5 dias úteis"
        };
      }
      return {};
    },
    async fetchSolicitationReclamation(context, { protocolo, observacao, anexosN }) {
      const pictures = (anexosN ?? []).filter(p => p.source).map(p => p.source);
      const response = await SolicitarOuvidoria({
        vinculo: context.rootState.saude.vinculo,
        protocolo: protocolo,
        assunto: 1,
        observacao: observacao,
        anexosN: pictures
      });
      if (response.Data) {
        return {
          protocol: response.Data.ProtocoloANS,
          ticket: response.Data.Chamado,
          validate: response.Data.Prazo
        };
      }
      return null;
    },
    async fetchProtocolDetailImage(context, { documento, original }) {
      return await ObterImagem({
        vinculo: context.rootState.saude.vinculo,
        documento,
        original
      });
    },
    async getDocumentUrl(context, { protocol }) {
      return await ObterDocumento({
        vinculo: context.rootState.saude.vinculo,
        protocolo: protocol
      });
    },
    async returnRequestTicket(context, { protocol, obs, pictures }) {
      checkUpdatePermission();
      const picturesSource = (pictures ?? []).filter(p => p.source).map(p => p.source);
      return await RetornoPendencia({
        vinculo: context.rootState.saude.vinculo,
        protocolo: protocol,
        observacao: obs,
        arquivos: picturesSource,
        classificacao: 10531
      });
    }
  },
  mutations: {
    SET_COMPLAINTS: (state, complaints) => {
      Vue.set(state, "lastFetch", new Date().getTime());
      Vue.set(state, "complaints", complaints);

      const beneficiaryFilters = [...new Set(complaints.map(p => p.beneficiaryName))].sort();
      const statusFilters = [...new Set(complaints.map(p => p.statusDsc))].sort();

      const filters = {
        beneficiary: beneficiaryFilters.map(f => ({
          label: f,
          isActive: state.filters.beneficiary?.find(current => current.label === f)?.isActive ?? false
        })),
        status: statusFilters.map(f => ({
          label: f,
          isActive: state.filters.status?.find(current => current.label === f)?.isActive ?? false
        }))
      };

      const beneficiaries = filters.beneficiary.filter(f => f.isActive) ?? [];
      const status = filters.status.filter(f => f.isActive) ?? [];
      filters.totalActived = beneficiaries.length + status.length;

      Vue.set(state, "filters", filters);
    },
    SET_SUCCESS_REQUEST: (state, successRequest) => {
      Vue.set(state, "successRequest", successRequest);
    },
    LOGOUT_USER: state => Object.keys(state).forEach(p => Vue.set(state, p, initialState[p])),
    RESET_CONTEXT: state => Object.keys(state).forEach(p => Vue.set(state, p, initialState[p])),
    SET_ACTIVED_SORT: (state, activedSort) => Vue.set(state, "activedSort", activedSort),
    SET_FILTERS: (state, filters) => Vue.set(state, "filters", filters),
    CLEAN_SORT_AND_FILTERS: state => {
      const filters = JSON.parse(JSON.stringify(state.filters));
      if (filters.status) filters.status.forEach(f => (f.isActive = false));
      if (filters.beneficiary) filters.beneficiary.forEach(f => (f.isActive = false));
      filters.totalActived = 0;
      filters.selectedBeneficiary = "";
      Vue.set(state, "filters", filters);
      Vue.set(state, "activedSort", "");
    },
    SET_NEW_SOLICITATION: (state, newSolicitation) => {
      state.newSolicitation = {
        ...state.newSolicitation,
        ...newSolicitation
      };
    },
    CLEAN_NEW_SOLICITATION: state => {
      Vue.set(state, "newSolicitation.protocolo", "");
      Vue.set(state, "newSolicitation.observacao", "");
      Vue.set(state, "newSolicitation.anexosN", []);
    }
  },
  getters: {
    filteredRequests: context => {
      let protocols = context.complaints?.length ? JSON.parse(JSON.stringify(context.complaints)) : [];
      if (protocols.length === 0) return [];

      const beneficiaries = context.filters?.beneficiary.filter(f => f.isActive) ?? [];
      if (beneficiaries?.length) {
        protocols = protocols.filter(p => !!beneficiaries.find(f => f.label === p.beneficiaryName));
        if (protocols.length === 0) return [];
      }
      const status = context.filters?.status.filter(f => f.isActive) ?? [];
      if (status?.length) {
        protocols = protocols.filter(p => !!status.find(f => f.label === p.statusDsc));
        if (protocols.length === 0) return [];
      }

      const sorts = {
        dateToNum(d) {
          if (!d || !d.includes("/")) return 0;
          d = d.split("/");
          return new Date(d[2], Number(d[1]) - 1, d[0]).getTime();
        },
        "Mais recentes"() {
          protocols.sort((a, b) => {
            return this.dateToNum(b.createdAt) - this.dateToNum(a.createdAt);
          });
        },
        "Mais antigos"() {
          protocols.sort((a, b) => {
            return this.dateToNum(a.createdAt) - this.dateToNum(b.createdAt);
          });
        }
      };

      if (context.activedSort) sorts[context.activedSort]();

      return protocols;
    },
    sortedComplaints: state => {
      const complaints = [...state.complaints];
      switch (state.activedSort) {
        case "Mais recentes":
          return complaints.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        case "Mais antigos":
          return complaints.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        default:
          return complaints;
      }
    }
  }
};
